/**
 * Handing the browser a file the page just built.
 *
 * Two things go wrong when this is written by hand, and both are silent:
 *
 * The link must be inside whatever is on top. `dialog.showModal()` makes the
 * rest of the document inert, and an inert element's activation behaviour
 * never runs — so an anchor appended to `document.body` and clicked from a
 * modal does nothing at all. Every download this app offers from inside a
 * dialog (a CSV template, a finished export) went that way.
 *
 * And the object URL must outlive the click. Revoking it in the same tick
 * cancels the download it just started, which shows up as "sometimes it works"
 * — usually on a fast machine with a small file.
 */

/** The top layer if one is open, so the click is not dispatched into an inert tree. */
function host(): HTMLElement {
  const open = document.querySelectorAll("dialog[open]");
  return (open[open.length - 1] as HTMLElement | undefined) ?? document.body;
}

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement("a"), { href: url, download: name });
  // Hidden rather than absent: a link has to be in the document for `download`
  // to be honoured, and in the layout for no longer than the click takes.
  link.style.display = "none";
  host().append(link);
  link.click();
  link.remove();
  // Long enough for the browser to have started reading it, then let go.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The same, for text this page wrote: a CSV, a template, a log. */
export function downloadText(text: string, name: string, type = "text/csv"): void {
  downloadBlob(new Blob([text], { type }), name);
}

/**
 * The browser's own "save as" picker, where there is one.
 *
 * Typed here rather than taken from `lib.dom`: the File System Access API is
 * not in every TypeScript DOM library, and this needs only the two calls.
 */
type SavePicker = (options: { suggestedName?: string }) => Promise<{
  createWritable: () => Promise<{
    write: (data: Blob) => Promise<void>;
    close: () => Promise<void>;
  }>;
}>;

const savePicker = (): SavePicker | null => {
  const fn = (window as unknown as { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
  return typeof fn === "function" ? fn.bind(window) : null;
};

/**
 * Save a file the page is about to fetch, and say whether it was really saved.
 *
 * `downloadBlob` cannot answer that. It clicks an `<a download>`, and from that
 * moment the browser owns the file — there is no event for "the person pressed
 * Save", none for Cancel, and none for the write finishing. A success message
 * sent after that click is a guess, and on a browser set to ask where to save
 * it appears while the dialog is still open, over a file nobody has saved yet.
 *
 * Where `showSaveFilePicker` exists it answers exactly: it resolves once the
 * handle is chosen and rejects with `AbortError` when the dialog is dismissed.
 *
 * **The picker is opened before `fetch` runs, not after.** It needs the user
 * activation of the click that started this, and that activation expires in a
 * few seconds — so a slow download would leave the picker refused with a
 * `SecurityError` precisely when the file is large enough to be worth saving.
 * Choosing first costs nothing: cancelling now also saves a pointless request.
 *
 * Returns `"cancelled"` when the person dismissed the dialog, and `"saved"`
 * otherwise — which on a browser with no picker means "handed to the browser",
 * the most that can be known there.
 */
export async function saveFile(
  name: string,
  fetchBlob: () => Promise<Blob>,
): Promise<"saved" | "cancelled"> {
  const choose = savePicker();
  if (!choose) {
    downloadBlob(await fetchBlob(), name);
    return "saved";
  }

  let handle;
  try {
    handle = await choose({ suggestedName: name });
  } catch (error) {
    if ((error as DOMException | undefined)?.name === "AbortError") return "cancelled";
    // Refused for some other reason — an insecure context, an activation that
    // expired, a browser that has the method but not the permission. A
    // download is not worth failing over the way it is saved.
    downloadBlob(await fetchBlob(), name);
    return "saved";
  }

  const blob = await fetchBlob();
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
  return "saved";
}
